/**
 * Canvas-based team flag textures for backpack and planted flags.
 * Each team gets a unique emblem drawn on their accent color background.
 */

import * as THREE from 'three';
import { TEAMS } from '../constants';

const textureCache = new Map<number, THREE.CanvasTexture>();

/**
 * Get (or create) a flag texture for the given team index.
 * Returns a 64x64 canvas texture with the team emblem.
 */
export function getFlagTexture(teamIndex: number): THREE.CanvasTexture {
  const cached = textureCache.get(teamIndex);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;

  const team = TEAMS[teamIndex % TEAMS.length];

  // Background: accent color
  ctx.fillStyle = team.accent;
  ctx.fillRect(0, 0, 64, 64);

  // Draw emblem
  ctx.fillStyle = team.detail;
  ctx.strokeStyle = team.visor;
  ctx.lineWidth = 2;

  drawEmblem(ctx, teamIndex % 10);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  textureCache.set(teamIndex, texture);
  return texture;
}

function drawEmblem(ctx: CanvasRenderingContext2D, emblemIndex: number): void {
  const cx = 32;
  const cy = 32;

  switch (emblemIndex) {
    case 0: // Star (Sidereal Conservancy)
      drawStar(ctx, cx, cy, 5, 18, 8);
      break;
    case 1: // Plant/leaf (Crater & Bloom)
      drawLeaf(ctx, cx, cy);
      break;
    case 2: // Spiral (TerraGently)
      drawSpiral(ctx, cx, cy);
      break;
    case 3: // Orbital ring (GreenOrbit)
      drawOrbitalRing(ctx, cx, cy);
      break;
    case 4: // Planet in box (BioSphere Direct)
      drawPlanetInBox(ctx, cx, cy);
      break;
    case 5: // Crossed tools (AstraTurf)
      drawCrossedTools(ctx, cx, cy);
      break;
    case 6: // Bar chart (Gentle Futures)
      drawBarChart(ctx, cx, cy);
      break;
    case 7: // Globe (PlanetCare)
      drawGlobe(ctx, cx, cy);
      break;
    case 8: // Chevrons (Orbital Reclamation)
      drawChevrons(ctx, cx, cy);
      break;
    case 9: // Shield + leaf (EcoShield)
      drawShieldLeaf(ctx, cx, cy);
      break;
  }
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  points: number, outer: number, inner: number
): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const angle = (i * Math.PI) / points - Math.PI / 2;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function drawLeaf(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy - 20);
  ctx.quadraticCurveTo(cx + 20, cy - 10, cx, cy + 20);
  ctx.quadraticCurveTo(cx - 20, cy - 10, cx, cy - 20);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Stem
  ctx.beginPath();
  ctx.moveTo(cx, cy - 20);
  ctx.lineTo(cx, cy + 20);
  ctx.stroke();
}

function drawSpiral(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.beginPath();
  for (let i = 0; i < 100; i++) {
    const t = i / 100;
    const angle = t * Math.PI * 4;
    const r = 4 + t * 18;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.lineWidth = 2;
}

function drawOrbitalRing(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Planet
  ctx.beginPath();
  ctx.arc(cx, cy, 10, 0, Math.PI * 2);
  ctx.fill();
  // Ring (ellipse)
  ctx.beginPath();
  ctx.ellipse(cx, cy, 22, 8, -0.3, 0, Math.PI * 2);
  ctx.stroke();
}

function drawPlanetInBox(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Box
  ctx.strokeRect(cx - 18, cy - 18, 36, 36);
  // Planet
  ctx.beginPath();
  ctx.arc(cx, cy, 12, 0, Math.PI * 2);
  ctx.fill();
}

function drawCrossedTools(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.lineWidth = 3;
  // Tool 1 (diagonal)
  ctx.beginPath();
  ctx.moveTo(cx - 16, cy - 16);
  ctx.lineTo(cx + 16, cy + 16);
  ctx.stroke();
  // Tool 2 (other diagonal)
  ctx.beginPath();
  ctx.moveTo(cx + 16, cy - 16);
  ctx.lineTo(cx - 16, cy + 16);
  ctx.stroke();
  // Center circle
  ctx.beginPath();
  ctx.arc(cx, cy, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = 2;
}

function drawBarChart(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  const barW = 8;
  const heights = [14, 22, 18, 26];
  const startX = cx - (heights.length * barW + (heights.length - 1) * 3) / 2;
  for (let i = 0; i < heights.length; i++) {
    const x = startX + i * (barW + 3);
    const h = heights[i];
    ctx.fillRect(x, cy + 18 - h, barW, h);
    ctx.strokeRect(x, cy + 18 - h, barW, h);
  }
}

function drawGlobe(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Outer circle
  ctx.beginPath();
  ctx.arc(cx, cy, 18, 0, Math.PI * 2);
  ctx.stroke();
  // Horizontal line
  ctx.beginPath();
  ctx.moveTo(cx - 18, cy);
  ctx.lineTo(cx + 18, cy);
  ctx.stroke();
  // Vertical ellipse
  ctx.beginPath();
  ctx.ellipse(cx, cy, 8, 18, 0, 0, Math.PI * 2);
  ctx.stroke();
}

function drawChevrons(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.lineWidth = 3;
  for (let i = 0; i < 3; i++) {
    const y = cy - 14 + i * 14;
    ctx.beginPath();
    ctx.moveTo(cx - 14, y);
    ctx.lineTo(cx, y + 10);
    ctx.lineTo(cx + 14, y);
    ctx.stroke();
  }
  ctx.lineWidth = 2;
}

function drawShieldLeaf(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  // Shield shape
  ctx.beginPath();
  ctx.moveTo(cx, cy - 22);
  ctx.lineTo(cx + 18, cy - 10);
  ctx.lineTo(cx + 14, cy + 12);
  ctx.lineTo(cx, cy + 22);
  ctx.lineTo(cx - 14, cy + 12);
  ctx.lineTo(cx - 18, cy - 10);
  ctx.closePath();
  ctx.stroke();
  // Small leaf inside
  ctx.beginPath();
  ctx.moveTo(cx, cy - 8);
  ctx.quadraticCurveTo(cx + 10, cy, cx, cy + 10);
  ctx.quadraticCurveTo(cx - 10, cy, cx, cy - 8);
  ctx.closePath();
  ctx.fill();
}
